import { useState } from 'react';
import axios from 'axios';
import { MapPin, Mail, Phone, Facebook, Instagram, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { usePreviewValue } from '../contexts/CMSPreviewContext';
import HoneypotField from './HoneypotField';
import { useI18n } from '../contexts/I18nContext';

const INTEREST_KEYS = [
  'contact.interest.programs',
  'contact.interest.rental',
  'contact.interest.volunteer',
  'contact.interest.partner',
  'contact.interest.donate',
  'contact.interest.other',
];

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', interest: '', message: '' });
  const [hp, setHp] = useState('');
  const h1 = usePreviewValue('contact', 'headline_1', t('contact.title1') + ' ' + t('contact.title2'));
  const h2 = usePreviewValue('contact', 'headline_2', t('contact.title3'));
  const desc = usePreviewValue('contact', 'description', t('contact.subtitle'));
  const addr1 = usePreviewValue('contact', 'address_line_1', '160 Hollis Street');
  const addr2 = usePreviewValue('contact', 'address_line_2', 'Framingham, MA, 01702');
  const email = usePreviewValue('contact', 'email', 'info@gemsatthearts.org');
  const phone = usePreviewValue('contact', 'phone', '(508) 405-2787');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email) {
      toast.error(t('contact.error'));
      return;
    }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/forms/submit`, {
        form_type: 'contact',
        source_page: 'home',
        data: { ...form, website: hp },
      });
    } catch (err) { /* silent fail - we still show success for UX */ }
    toast.success(t('contact.success'));
    setForm({ firstName: '', lastName: '', email: '', interest: '', message: '' });
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-[#E5614C] relative overflow-hidden">
      <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -bottom-32 -right-20 w-[480px] h-[480px] rounded-full bg-[#2A1B1A]/15 blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 relative">
        <div className="text-white">
          <h2 className="font-serif-display text-[42px] sm:text-[52px] lg:text-[64px] leading-[1.02] font-bold">
            {h1} <span className="italic">{h2}</span>
          </h2>
          <p className="mt-6 text-white/85 text-[17px] leading-relaxed max-w-lg">
            {desc}
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-white/70 text-[13px]">{t('contact.visit')}</div>
                <div className="font-semibold">{addr1}</div>
                <div className="font-semibold">{addr2}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-white/70 text-[13px]">{t('contact.email')}</div>
                <div className="font-semibold">{email}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center shrink-0">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-white/70 text-[13px]">{t('contact.phone')}</div>
                <div className="font-semibold">{phone}</div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex gap-3">
            {[
              { Icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61588942857783', label: 'Facebook' },
              { Icon: Instagram, href: 'https://www.instagram.com/gemsatthearts', label: 'Instagram' },
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="h-11 w-11 rounded-full bg-white/15 hover:bg-white hover:text-[#E5614C] border border-white/25 flex items-center justify-center transition-colors duration-300"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-[28px] p-7 lg:p-10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.4)] relative"
        >
          <HoneypotField value={hp} onChange={setHp} />
          <h3 className="font-serif-display text-[28px] lg:text-[34px] font-bold text-[#2A1B1A]">{t('contact.formTitle')}</h3>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="fn" className="text-[#2A1B1A] text-[13px] font-semibold">{t('contact.firstName')}</Label>
              <Input
                id="fn"
                placeholder={t('contact.firstNamePh')}
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className="h-12 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
                data-testid="contact-first-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ln" className="text-[#2A1B1A] text-[13px] font-semibold">{t('contact.lastName')}</Label>
              <Input
                id="ln"
                placeholder={t('contact.lastNamePh')}
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className="h-12 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
                data-testid="contact-last-name"
              />
            </div>
          </div>
          <div className="mt-4 space-y-1.5">
            <Label htmlFor="em" className="text-[#2A1B1A] text-[13px] font-semibold">{t('contact.emailLabel')}</Label>
            <Input
              id="em"
              type="email"
              placeholder={t('contact.emailPh')}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="h-12 rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
              data-testid="contact-email"
            />
          </div>
          <div className="mt-4 space-y-1.5">
            <Label className="text-[#2A1B1A] text-[13px] font-semibold">{t('contact.interest')}</Label>
            <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
              <SelectTrigger className="h-12 rounded-xl border-[#2A1B1A]/15 focus:ring-[#E5614C]/40" data-testid="contact-interest-trigger">
                <SelectValue placeholder={t('contact.interestPh')} />
              </SelectTrigger>
              <SelectContent>
                {INTEREST_KEYS.map((k) => (
                  <SelectItem key={k} value={t(k)}>{t(k)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-4 space-y-1.5">
            <Label htmlFor="msg" className="text-[#2A1B1A] text-[13px] font-semibold">{t('contact.message')}</Label>
            <Textarea
              id="msg"
              rows={4}
              placeholder={t('contact.messagePh')}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="rounded-xl border-[#2A1B1A]/15 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C] resize-none"
              data-testid="contact-message"
            />
          </div>
          <Button
            type="submit"
            className="mt-6 w-full h-13 rounded-xl bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold py-3.5 btn-press"
            data-testid="contact-submit"
          >
            {t('contact.send')} <Send className="h-4 w-4 ml-2" />
          </Button>
        </form>
      </div>
    </section>
  );
}
